/**
 * A chord you already played, looked at properly.
 *
 * ## Why this exists
 *
 * Tapping a history node used to rewind to it on the spot. That is a target the size of a
 * chord symbol, in a rail you scroll with your thumb, silently discarding every chord
 * after it — accident-prone, and opaque even when it was what you meant, because nothing
 * on screen told you what you were going back *to*. The owner asked for the other order:
 * "I want to be able to go back to a previous thing and view it in more detail, and then
 * maybe even explore the graph from that place."
 *
 * So the tap opens this, and this offers the rewind. Nothing about the rewind itself has
 * changed — same `stepBackTo`, same `STEP_BACK`, same absence of a redo. This is entirely
 * presentation: a preview in front of an action that already existed.
 *
 * ## What it can honestly show
 *
 * The chord, its notes, when it was played and how it was chosen are all on the node
 * itself and are always available. The *relation* it arrived by is not: it lives on the
 * suggestion, in the branch set belonging to the node before it, and the reducer prunes
 * branch sets more than one step back. So it is looked up and simply omitted when it is
 * gone — the same honesty `GhostStubs` already practises. Inventing a plausible relation
 * for a move whose evidence has been collected would be worse than saying nothing.
 *
 * The long explanation follows the same rule. Where the suggestion survives, "why does
 * this work" is exactly the request the branch card would have made — same
 * `fetchExplanation`, same suggestion, and therefore the same cache entry, so a paragraph
 * already read this session is simply there. Where it does not survive, there is nothing
 * to ask about and nothing is offered.
 *
 * And On the Go it can be *heard*: the chord is on the node, so "Hear it" needs nothing
 * that could have been pruned. It plays through the same `previewChord` the branch card
 * uses, which is the same voicing the chord had when it landed — this is what that chord
 * sounded like, not an approximation of it. Never at the piano; the microphone is open.
 */

import { useCallback, useEffect, useState } from 'react';
import type { BranchSuggestion, CanonicalChord, SessionNode, SessionState } from '@hc/shared';
import { BottomSheet } from '../system/BottomSheet.js';
import { MiniKeyboard } from '../system/MiniKeyboard.js';
import { pitchClassNames } from '../system/chordReading.js';
import type { ExplanationFetcher } from '../system/sessionView.js';
import { cachedExplanation, useExplanation } from '../system/useExplanation.js';

export interface HistoryDetailSheetProps {
  open: boolean;
  /** Null while the sheet is closing; the sheet keeps its shape until it has gone. */
  node: SessionNode | null;
  state: SessionState;
  fetchExplanation: ExplanationFetcher | undefined;
  /** The existing `view.stepBackTo`. This sheet does not know what a `STEP_BACK` is. */
  onExplore: (nodeId: string) => void;
  /**
   * `view.previewChord`: hear this chord again, without going back to it.
   *
   * It sits beside the notes rather than with the actions at the foot, and that is the
   * whole distinction this sheet is built on — the actions are three ways of leaving, and
   * hearing a chord is not one of them. Absent at the piano, and with it the button.
   */
  onPreview?: ((chord: CanonicalChord) => void) | undefined;
  /**
   * `view.saveSketch`, exactly as `SessionHeader` is handed it — the same act, offered at
   * the same moment for the same reason. Absent hides "Keep it first".
   */
  onSave?: (() => void | Promise<unknown>) | undefined;
  onClose: () => void;
}

/** How the chord got here, in the player's words rather than the reducer's. */
const SOURCE_WORDS: Readonly<Record<SessionNode['source'], string>> = Object.freeze({
  audio: 'played',
  manual: 'tapped',
  correction: 'corrected',
});

/**
 * When this chord happened, as time into the session.
 *
 * Relative, and deliberately not a wall clock. `playedAt` is `performance.now()`, whose
 * origin is *this page load* — mixing it with `startedAt`, which is `Date.now()`, produces
 * a confident and completely wrong time of day, and a sketch continued from yesterday has
 * been rebased onto the current clock anyway (`sketchbook/continueFrom.ts`). Measured from
 * the first node, which is the only origin both of those cases agree about.
 */
export function elapsedLabel(node: SessionNode, path: readonly SessionNode[]): string {
  const first = path[0];
  if (!first || node.id === first.id) return 'at the start';
  const seconds = Math.max(0, Math.round((node.playedAt - first.playedAt) / 1000));
  if (seconds === 0) return 'at the start';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  const clock = `${minutes}:${String(rest).padStart(2, '0')}`;
  return `${clock} into this session`;
}

/** The suggestion this node was reached by, if that branch set is still retained. */
export function arrivedBySuggestion(
  state: SessionState,
  node: SessionNode,
): BranchSuggestion | null {
  if (!node.fromSuggestionId) return null;
  const index = state.path.findIndex((entry) => entry.id === node.id);
  if (index <= 0) return null;
  const previous = state.path[index - 1];
  if (!previous) return null;
  const set = state.branchSets[previous.id];
  return set?.suggestions.find((s) => s.id === node.fromSuggestionId) ?? null;
}

/** How many chords would be discarded by going back to this one. */
export function forwardCount(state: SessionState, node: SessionNode): number {
  const index = state.path.findIndex((entry) => entry.id === node.id);
  if (index < 0) return 0;
  return state.path.length - 1 - index;
}

export function HistoryDetailSheet({
  open,
  node,
  state,
  fetchExplanation,
  onExplore,
  onPreview,
  onSave,
  onClose,
}: HistoryDetailSheetProps): React.JSX.Element | null {
  /** The player asked for the paragraph. Reset per node — it is a question about one move. */
  const [asked, setAsked] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const nodeId = node?.id ?? null;

  useEffect(() => {
    setAsked(false);
    setKeeping(false);
  }, [nodeId, open]);

  const suggestion = node ? arrivedBySuggestion(state, node) : null;
  // Already read this session? Then it is not an offer, it is just there.
  const recalled = cachedExplanation(suggestion?.id) !== null;
  const load = useExplanation(suggestion, fetchExplanation, open && (asked || recalled));

  const keepThenExplore = useCallback(() => {
    if (!node) return;
    const nodeIdToExplore = node.id;
    if (!onSave) {
      onExplore(nodeIdToExplore);
      return;
    }
    setKeeping(true);
    // Rewind whether or not the save succeeded — the same reasoning `SessionHeader` gives:
    // a storage failure has already been reported as a chip, and refusing to move because
    // the sketchbook is unavailable traps somebody in a session over a second problem.
    void Promise.resolve(onSave()).then(
      () => {
        setKeeping(false);
        onExplore(nodeIdToExplore);
      },
      () => {
        setKeeping(false);
        onExplore(nodeIdToExplore);
      },
    );
  }, [node, onExplore, onSave]);

  if (!node) return null;

  const ahead = forwardCount(state, node);
  const notes = pitchClassNames(node.chord, node.keyAtTime);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={node.chord.symbol}
      eyebrow={
        suggestion ? (
          <>
            {suggestion.relationLabel}
            {suggestion.romanNumeral ? ` · ${suggestion.romanNumeral}` : ''}
          </>
        ) : (
          <>a chord you {SOURCE_WORDS[node.source]}</>
        )
      }
      testId="hc-history-detail"
    >
      <div className="hc-explanation__head">
        <MiniKeyboard
          pitchClasses={node.chord.pitchClasses}
          bassPc={node.chord.bassPc}
          label={`${node.chord.symbol} notes`}
        />
        <p className="hc-history-detail__notes" data-testid="hc-history-detail-notes">
          {notes}
        </p>
        {/*
          With the notes, because it is the same statement made twice — these are the
          pitches, and this is what they sound like. Nothing else in this sheet changes:
          hearing a chord you already played is not a step towards going back to it, and it
          would be misread as one down among the three ways of leaving.
        */}
        {onPreview ? (
          <button
            type="button"
            className="hc-button hc-button--quiet hc-history-detail__hear"
            data-testid="hc-history-detail-hear"
            onClick={() => onPreview(node.chord)}
            aria-label={`Hear ${node.chord.symbol}`}
          >
            Hear it
          </button>
        ) : null}
      </div>

      <p className="hc-sheet__quiet" data-testid="hc-history-detail-when">
        {SOURCE_WORDS[node.source]} · {elapsedLabel(node, state.path)}
      </p>

      {suggestion ? (
        <p className="hc-explanation__short">{suggestion.shortDescription}</p>
      ) : (
        /*
          Not an apology and not an error. Going somewhere nobody offered is the product
          working; so is a branch set the reducer has since pruned. Either way the honest
          line is that there is no annotation to show, not a fabricated one.
        */
        <p className="hc-sheet__quiet" data-testid="hc-history-detail-unlabelled">
          {node.fromSuggestionId
            ? 'The ideas offered here are no longer kept, so there is nothing more to say about how you arrived.'
            : 'You went your own way to this one.'}
        </p>
      )}

      {suggestion && fetchExplanation ? (
        <div className="hc-history-detail__why">
          {load.status === 'idle' ? (
            <button
              type="button"
              className="hc-button hc-button--quiet"
              data-testid="hc-history-detail-why"
              onClick={() => setAsked(true)}
            >
              Why does this work?
            </button>
          ) : null}

          {load.status === 'loading' ? (
            <div
              className="hc-explanation__skeleton"
              data-testid="hc-history-detail-loading"
              aria-busy="true"
            >
              <span />
              <span />
              <span />
            </div>
          ) : null}

          {load.status === 'ready' ? (
            <p className="hc-explanation__long" data-testid="hc-history-detail-explanation">
              {load.text}
            </p>
          ) : null}

          {load.status === 'failed' ? (
            <p className="hc-sheet__quiet" data-testid="hc-history-detail-failed">
              The longer note is not available right now.
            </p>
          ) : null}
        </div>
      ) : null}

      {/*
        The cost of the rewind, in one line, before the button that pays it. It says what
        survives as well as what goes: the autosave is real and the sketchbook is one tap
        away, so this is information rather than a warning, and it is not phrased as one.
      */}
      {ahead > 0 ? (
        <p className="hc-sheet__quiet" data-testid="hc-history-detail-cost">
          The {ahead === 1 ? 'chord' : `${ahead} chords`} after this stay in this session&rsquo;s
          autosave, and you can keep the whole line as a sketch first.
        </p>
      ) : null}

      {/*
        Three weights, left to right: leaving, keeping, going. The same order and the same
        hierarchy the header's confirm uses, because it is the same question.
      */}
      <div className="hc-sheet__actions">
        <button
          type="button"
          className="hc-button"
          data-testid="hc-history-detail-close"
          onClick={onClose}
        >
          Close
        </button>
        {onSave && ahead > 0 ? (
          <button
            type="button"
            className="hc-button hc-button--quiet"
            data-testid="hc-history-detail-keep"
            disabled={keeping}
            onClick={keepThenExplore}
          >
            {keeping ? 'Keeping…' : 'Keep it first'}
          </button>
        ) : null}
        <button
          type="button"
          className="hc-button hc-button--primary"
          data-testid="hc-history-detail-explore"
          onClick={() => onExplore(node.id)}
        >
          Explore from here
        </button>
      </div>
    </BottomSheet>
  );
}

export default HistoryDetailSheet;
